import React, { useState } from 'react';
import Button from '@material-ui/core/Button';
import { makeStyles } from '@material-ui/core/styles';
import ConfirmationModal from '../../components/ConfirmationModal';
import { useNotification } from '../../contexts/NotificationContext';
import { LoanService } from '../../services';
import { isSuccessfulResponse } from '../../utils/responseHelpers';

const useStyles = makeStyles(() => ({
    retryButton: {
        textTransform: 'none',
    },
}));

// Only these NLS operations have a corresponding resend endpoint.
const RETRY_HANDLERS = {
    funding_update: LoanService.sendNLSFundingUpdate,
    payment_update: LoanService.sendNLSPaymentUpdate,
    refund_update: LoanService.sendNLSRefundUpdate,
};

// Retry is offered only when the original request errored server-side (5xx) or
// never recorded an HTTP status (blank).
const isRetryableStatus = (responseStatus) =>
    responseStatus == null || (responseStatus >= 500 && responseStatus <= 599);

const RetryNlsUpdateCell = ({ log }) => {
    const classes = useStyles();
    const { createNotification, createErrorNotification } = useNotification();
    const [showConfirmation, setShowConfirmation] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);

    const sendUpdate = RETRY_HANDLERS[log?.operation];
    if (!sendUpdate || !isRetryableStatus(log?.responseStatus)) return null;

    const toggleConfirmation = () => setShowConfirmation((open) => !open);

    // isProcessing keeps the grid Retry button disabled until the request
    // settles, so it can't be re-fired even if the user dismisses the modal
    // mid-flight. (ConfirmationModal also disables its own confirm button.)
    const handleConfirm = async () => {
        setIsProcessing(true);
        try {
            const response = await sendUpdate(log.requestPayload);

            if (isSuccessfulResponse(response)) {
                createNotification('NLS update resent successfully');
            } else {
                createErrorNotification('Failed to resend NLS update');
            }
        } catch (_error) {
            createErrorNotification('Failed to resend NLS update');
        } finally {
            setIsProcessing(false);
            setShowConfirmation(false);
        }
    };

    return (
        <>
            <ConfirmationModal
                title="Confirm"
                message="Are you sure you want to resend this update to NLS?"
                onConfirm={handleConfirm}
                onCancel={toggleConfirmation}
                showConfirmationBox={showConfirmation}
            />
            <Button
                variant="contained"
                color="primary"
                className={classes.retryButton}
                onClick={toggleConfirmation}
                disabled={isProcessing}
            >
                Retry
            </Button>
        </>
    );
};

export default RetryNlsUpdateCell;
